<template>
    <Head title="Reviews" />
    <BreezeAuthenticatedLayout>
        <template #header>
            <div class="grid grid-flow-col gap-4">
                <div>
                    <h2 class="text-xl font-semibold leading-tight text-gray-800">
                        Reviews
                    </h2>
                </div>
            </div>
        </template>
        <form method="post" action="/admin/reviews/export">
            <input type="hidden" name="_token" :value="this.$page.props.csrf">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4 items-end relative">
                <div>
                    <admin-app-label>Export Reviews</admin-app-label>
                    <select name="type" class="w-full">
                            <option v-for="option in exportOptions" v-bind:value="option.value">{{ option.text }}</option>
                    </select>
                </div>
                <div class="grid grid-rows-1 grid-flow-col gap-4">
                    <div class="grid grid-cols-3 gap-2">
                        <div class="w-full">
                            <admin-app-label>From</admin-app-label>
                            <input type="date" class="w-full" name="from" placeholder="From" required>
                        </div>
                        <div class="w-full">
                            <admin-app-label>To</admin-app-label>
                            <input type="date" class="w-full" name="to" placeholder="To" required>
                        </div>
                        <div class="w-full">
                            <admin-app-label>Include Unapproved</admin-app-label>
                            <input type="checkbox" name="non_approved" value="1">
                        </div>
                    </div>
                </div>
                <div>
                    <admin-link type="submit" class="inset-y-0 right-0 flex items-center h-full !py-3">Export</admin-link>
                </div>
            </div>
        </form>
        <BreadCrumbs :items="navItems"></BreadCrumbs>
        <div class="relative w-full py-12 my-6 shadow">
            <form @submit.prevent="form.get('reviews')">
                <admin-search-form v-model="form.search"></admin-search-form>
            </form>
            <div class="px-2 md:px-6 pt-2 md:pt-4 pb-2">
                <div class="inline-block min-w-full">
                    <table class="table table-auto w-full leading-normal">
                        <thead class="uppercase text-gray-600 text-xs font-semibold bg-gray-200">
                            <tr class="hidden lg:table-row">
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider hidden md:table-cell">&nbsp;</th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    Details
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    Date Created
                                    <Datepicker v-model="reviewDates" range @update:modelValue="filterDate" :enable-time-picker="false" :format="formatDatePicker" />
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    <select v-model="filterApproved" @change="applyFilter">
                                        <option v-for="option in options"
                                                v-bind:value="option.value"
                                                :disabled="option.disabled">{{ option.text }}
                                        </option>
                                    </select>
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    Reviewed
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    <select v-model="selectedWebsite" @change="applyFilter">
                                        <option v-for="website in websites"
                                                v-bind:value="website.value"
                                                :disabled="website.disabled">{{ website.text }}
                                        </option>
                                    </select>
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">
                                    <input-select
                                        multiple
                                        placeholder="Rating"
                                        :options="ratings"
                                        v-model="localRatings"
                                        @update:modelValue="applyFilter"
                                        :class="`!h-auto`"
                                    />
                                </th>
                                <th class="bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody class="my-3 flex-1 lg:flex-none">
                            <tr v-if="reviews.data.length"
                                v-for="review of reviews.data"
                                class="mb-2 lg:mb-0 border-2 lg:border-b-2 border-grey-200 lg:border-t lg:first:border-t-0 flex p-1 md:p-3 hover:bg-gray-100 lg:table-row flex-col w-full flex-wrap">
                                <td class="p-1 lg:p-5 lg:border-l-2 lg:border-b border-grey-200 bg-white text-sm md:text-base">
                                    <checkbox @click="multiApprove(review.id)" v-if="!review.approved"></checkbox>
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Title</label>
                                    {{ review.title }}
                                    <label @click="toggle(review.id)"
                                            class="block tracking-wider uppercase cursor-pointer mt-2"><span v-if="showing === review.id"> <font-awesome-icon
                                        icon="minus"
                                        class="mr-3"/>Hide</span><span v-else><font-awesome-icon icon="plus"
                                                                                                    class="mr-3"/> Show</span>
                                        Comments</label>
                                    <div class="mt-2 p-3 border border-grey-200 transition duration-150 ease-in-out"
                                            v-if="showing === review.id">
                                        {{ review.comment }}
                                    </div>
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Date Created</label>
                                    {{ showDate(review.review_date) }}
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Approved</label>
                                    {{ review.approved ? "Yes" : "No" }}
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Reviewed</label>
                                    {{ review.product.name }}
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Website</label>
                                    <span v-if="review.website">
                                        <a :href="`${review.website.url}`"
                                            target="_blank"><img :src="`/images/logos/tyre-sites/${review.website.name}.png`"
                                                                class="object-scale-down w-20"/></a>
                                    </span>
                                    <span v-else>N/A</span>
                                </td>
                                <td class="p-1 lg:p-5 lg:border-b border-grey-200 bg-white text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <label class="mb-3 bg-grey-100 border-b-2 border-grey-200 font-semibold px-5 py-3 uppercase text-sm text-left tracking-wider block lg:hidden !text-dark">Rating</label>
                                    <ReviewStarRating :flex="`flex-0`"
                                                        :size="`1x`"
                                                        :modelValue="review.rating"></ReviewStarRating>
                                </td>
                                <td class="px-5 py-5 bg-white text-center lg:text-left text-sm md:text-base"
                                    :class="{'bg-red text-white': !review.approved }">
                                    <div class="flex items-center">
                                        <admin-link class="bg-grey-100 text-dark"
                                                    :href="'/admin/reviews/' + review.id">
                                            View
                                        </admin-link>
                                        <admin-link type="button"
                                                    class="bg-red"
                                                    @click="showDeleteModal(review.id)">
                                            Delete
                                        </admin-link>
                                        <admin-delete-modal v-if="deleteModalIdToShow === review.id"
                                                            :msg="modalMessage"
                                                            :type="modalType"
                                                            @toggle-delete-modal="hideDeleteModal()"
                                                            @delete-item="deleteItem(review.id)"></admin-delete-modal>
                                    </div>
                                </td>
                            </tr>
                            <tr v-else>
                                <td class="py-6 bg-red" colspan="8" align="center">
                                    <p class="text-white font-bold">No reviews found</p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="ids.length" class="mt-4">
                        <admin-link type="button" @click="showReviewAcceptModal" class="bg-grey-100 text-dark">Approve Selected Reviews</admin-link>
                        <admin-accept-modal v-if="showAcceptModal" @toggle-accept-modal="hideReviewAcceptModal()" :title="`Are you sure you want to approve?`" :msg="`Approve the selected reviews`">
                            <Link @click="hideReviewAcceptModal" class="inline-flex items-center px-4 py-2 bg-primary text-white font-semibold text-xs uppercase tracking-widest" href="/admin/reviews/approve-multi" method="post" :data="{ ids: ids }" type="button">Approve Selected Reviews</Link>
                        </admin-accept-modal>
                    </div>
                    <admin-pagination class="mt-6" :links="reviews.links" />
                </div>
            </div>
        </div>
        <ToTopLink>To Top</ToTopLink>
    </BreezeAuthenticatedLayout>
</template>
<script>
import BreezeAuthenticatedLayout from '@/Layouts/Admin/Authenticated.vue';
import { Inertia } from '@inertiajs/inertia';
import {Head, Link, useForm} from '@inertiajs/inertia-vue3';
import AdminLink from "@/Components/Admin/AdminLink";
import AdminSearchForm from "@/Components/Admin/Forms/AdminSearchForm";
import AdminDeleteModal from "@/Components/Admin/AdminDeleteModal";
import AdminPagination from "@/Components/Admin/AdminPagination";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import ReviewStarRating from "@/Components/Reviews/ReviewStarRating";
import Checkbox from "@/Components/Checkbox";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import ToTopLink from "@/Components/Admin/ToTopLink";
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";
import AdminAcceptModal from "@/Components/Admin/AdminAcceptModal";
import { config } from '@fortawesome/fontawesome-svg-core';
import dayjs from "dayjs";
import { ref } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    components:
        {
            AdminAcceptModal,
            ToTopLink,
            AdminAppLabel,
            BreadCrumbs,
            AdminPagination,
            AdminLink,
            AdminDeleteModal,
            AdminSearchForm,
            Checkbox,
            Head,
            InputSelect,
            FontAwesomeIcon,
            ReviewStarRating,
            Link,
            BreezeAuthenticatedLayout,
            Datepicker
        },
    data: function()
    {
        return {
            filterApproved: this.approved,
            showing: false,
            options: [
                { value: "", text: 'Approved' },
                { value: '1', text: 'Yes' },
                { value: '0', text:  'No' }
            ],
            exportOptions: [
                { value: "all", text: "All Reviews" },
                { value: "website", text: "Websites" },
                { value: "fitter", text: "Fitters" },
                { value: "tyre", text: "Tyres" },
            ],
            ids: [],
            localRatings: this.rating,
            ratings: ["1", "2", "3", "4", "5"],
            navItems: [
                { title: 'Reviews', path: '/admin/reviews' },
            ],
            deleteModalIdToShow: false,
            showAcceptModal: false,
            modalMessage: 'Are you sure you want to remove this review?',
            modalType: 'Review',
            exportData: useForm({
                type : "",
                from: "",
                to: ""
            }),
            form: useForm({
                search: this.term
            }),
            selectedWebsite: this.website,
            websites: this.$trusted_checks.value ? [
                { value: '', text: 'Website' },
                { value: 'racshop', text: 'RAC Shop' }, 
                { value: 'racvhc', text: 'RAC Vehicle History Check' }
            ] : [
                { value: '', text: 'Website' },
                { value: 'asdatyres', text: 'ASDA Tyres' }, 
                { value: 'etyres', text: 'eTyres' },
                { value: 'ractyres', text: 'RAC Tyres' },
                { value: 'servicepoint', text: 'Service Point' }
            ],
            reviewDates: this.dates
        }
    },
    props: {
        reviews: Object,
        approved: String,
        rating: Array,
        term: String,
        website: String,
        dates: Array
    },
    methods: {
        toggle(id)
        {
            if(this.showing == id)
            {
                this.showing = false;
            }
            else
            {
                this.showing = id;
            }
        },
        multiApprove(value)
        {
            if(this.ids.includes(value))
            {
                // If in Array - Remove it
                const index = this.ids.indexOf(value);
                if(index > -1)
                {
                    this.ids.splice(index, 1);
                }
            }
            else
            {
                // If not in Array - Add it
                this.ids.push(value);
            }
        },
        applyFilter()
        {
            var params = {};
            if (this.term) {
                params.search = this.term;
            }
            if (this.reviewDates && this.reviewDates.length > 0) {
                params.dates = this.reviewDates;
            }
            if (this.filterApproved) {
                params.approved = this.filterApproved;
            }
            if (this.selectedWebsite) {
                params.website = this.selectedWebsite;
            }
            if (this.localRatings && this.localRatings.length > 0) {
                params.ratings = this.localRatings;
            }

            var param_string = new URLSearchParams(params);
            if (param_string) {
                param_string = "?"+param_string;
            }

            Inertia.visit("/admin/reviews"+param_string);
        },
        filterDate(dates)
        {
            if (dates) {
                var start_date = dates[0].getFullYear()+'-'+(dates[0].getMonth()+1)+'-'+dates[0].getDate();
                var end_date = dates[1].getFullYear()+'-'+(dates[1].getMonth()+1)+'-'+dates[1].getDate();

                this.reviewDates = start_date+","+end_date;
            }

            this.applyFilter();
        },
        formatDatePicker(dates)
        {
            var date_string = null;
            if (dates[0]) {
                date_string = dates[0].getDate()+'/'+(dates[0].getMonth()+1)+'/'+dates[0].getFullYear();
            }
            if (dates[1]) {
                date_string += " - " + dates[1].getDate()+'/'+(dates[1].getMonth()+1)+'/'+dates[1].getFullYear()
            }

            return date_string;
        },
        showReviewAcceptModal()
        {
            this.showAcceptModal = true;
        },
        hideReviewAcceptModal()
        {
            this.showAcceptModal = false;
        },
        showDeleteModal(id)
        {
            this.deleteModalIdToShow = id;
        },
        hideDeleteModal()
        {
            this.deleteModalIdToShow = false;
        },
        deleteItem(id)
        {
            this.$inertia.delete('/admin/reviews/'+id);
        },
        showDate(date)
        {
            return dayjs(date).format('DD-MM-YYYY')
        }
    }
}
</script>
